import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useLibraryData, type LibraryPrompt, type LibraryDraft } from '@/hooks/useLibraryData';
import { MainLayout } from '@/components/layout/MainLayout';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LibraryCard } from '@/components/library/LibraryCard';
import { DraftCard } from '@/components/library/DraftCard';
import { LibraryEmptyState } from '@/components/library/LibraryEmptyState';
import { PromptDetailModal } from '@/components/shared/PromptDetailModal';
import { RemixersModal } from '@/components/feed/RemixersModal';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { classifyError } from '@/lib/fetchWithTimeout';
import type { FavoritePrompt } from '@/hooks/useFavorites';
import { SlidersHorizontal } from 'lucide-react';

export type LibraryTab = 'drafts' | 'recent' | 'favorites';
type SortOption = 'date-desc' | 'date-asc' | 'points-desc' | 'points-asc';

export default function Library() {
  const { user, membership } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { recent, drafts, favorites, isLoading } = useLibraryData(user?.id);

  // Get tab from URL params, default to 'recent'
  const tabParam = searchParams.get('tab') as LibraryTab | null;
  const activeTab: LibraryTab = tabParam && ['drafts', 'recent', 'favorites'].includes(tabParam)
    ? tabParam
    : 'recent';

  const [selectedPrompt, setSelectedPrompt] = useState<LibraryPrompt | null>(null);
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [sortOption, setSortOption] = useState<SortOption>('date-desc');
  const [draftSelectMode, setDraftSelectMode] = useState(false);
  const [selectedDraftIds, setSelectedDraftIds] = useState<Set<string>>(new Set());
  const [draftDeleteIds, setDraftDeleteIds] = useState<string[]>([]);

  // Orphan scoring state
  const [scoringDraftId, setScoringDraftId] = useState<string | null>(null);

  // Remixers modal state
  const [showRemixersModal, setShowRemixersModal] = useState(false);
  const [remixersPromptId, setRemixersPromptId] = useState<string | null>(null);
  const [remixersCount, setRemixersCount] = useState(0);

  useEffect(() => {
    setCategoryFilter('All');
    setDraftSelectMode(false);
    setSelectedDraftIds(new Set());
  }, [activeTab]);

  const handleCloseModal = () => {
    setSelectedPrompt(null);
  };

  const handleBuildPrompt = () => {
    navigate('/prompt');
  };

  const handleContinueDraft = (draft: LibraryDraft) => {
    navigate(`/build/${draft.conversation_id}`, { state: { returnTo: '/library?tab=drafts' } });
  };

  const handleRemix = (prompt: LibraryPrompt) => {
    navigate('/feed', { state: { remixPrompt: prompt.best_prompt || prompt.original_content } });
  };

  const handleViewRemixers = (prompt: LibraryPrompt) => {
    setRemixersPromptId(prompt.score_id);
    setRemixersCount(prompt.remix_count || 0);
    setShowRemixersModal(true);
  };

  const handleFavoriteViewRemixers = (fav: FavoritePrompt) => {
    setRemixersPromptId(fav.score_id);
    setRemixersCount(fav.remix_count || 0);
    setShowRemixersModal(true);
  };

  const invalidateLibraryLists = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['library-recent'] });
    queryClient.invalidateQueries({ queryKey: ['library-drafts'] });
    queryClient.invalidateQueries({ queryKey: ['library-counts'] });
    queryClient.invalidateQueries({ queryKey: ['user-favorites'] });
  }, [queryClient]);

  const handleHideOrDeletePrompt = useCallback(() => {
    invalidateLibraryLists();
  }, [invalidateLibraryLists]);

  const handleDeleteDrafts = useCallback(async (draftIds: string[]) => {
    if (!user || draftIds.length === 0) return;

    const { error } = await supabase
      .from('builder_conversations')
      .update({ status: 'abandoned' })
      .eq('user_id', user.id)
      .in('id', draftIds);

    if (error) {
      console.error('Error deleting drafts:', error);
      toast.error(draftIds.length === 1 ? 'Failed to delete draft' : 'Failed to delete drafts');
      return;
    }

    toast.success(draftIds.length === 1 ? 'Draft deleted' : `${draftIds.length} drafts deleted`);
    setSelectedDraftIds(new Set());
    setDraftSelectMode(false);
    invalidateLibraryLists();
  }, [invalidateLibraryLists, user]);

  const handleDeleteDraft = useCallback((draft: LibraryDraft) => {
    setDraftDeleteIds([draft.conversation_id]);
  }, []);

  const handleScoreOrphan = useCallback(async (draft: LibraryDraft) => {
    if (!draft.final_prompt || !user || scoringDraftId) return;
    setScoringDraftId(draft.conversation_id);
    try {
      const { data, error } = await supabase.functions.invoke('score-submission', {
        body: {
          content: draft.final_prompt,
          submission_type: 'prompt_single',
          source: 'app',
          conversation_id: draft.conversation_id,
        },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      if (data.sensitivity_flag) {
        toast.info('This prompt contains sensitive information and has been saved privately in your library.');
      } else {
        toast.success('Prompt scored successfully!');
      }
      invalidateLibraryLists();

      const transformedResult = {
        ui: {
          ...data.analysis,
          score_id: data.score_id,
          submission_id: data.submission_id,
          is_public: !data.sensitivity_flag,
          original_score: data.original_score,
          improved_score: data.overall_score,
          user_id: user.id,
        },
        telemetry: {
          tags: data.analysis?.tags || [],
        },
      };

      navigate(`/prompt_score/${data.score_id}`, { state: { result: transformedResult } });
    } catch (err) {
      const classified = classifyError(err);
      toast.error(classified.message);
    } finally {
      setScoringDraftId(null);
    }
  }, [user, scoringDraftId, invalidateLibraryLists, navigate]);

  const handleConfirmDeleteDrafts = useCallback(async () => {
    const ids = draftDeleteIds;
    setDraftDeleteIds([]);
    await handleDeleteDrafts(ids);
  }, [draftDeleteIds, handleDeleteDrafts]);

  const handleToggleDraftSelected = useCallback((draft: LibraryDraft) => {
    setSelectedDraftIds((current) => {
      const next = new Set(current);
      if (next.has(draft.conversation_id)) {
        next.delete(draft.conversation_id);
      } else {
        next.add(draft.conversation_id);
      }
      return next;
    });
  }, []);

  const handleCancelDraftSelect = () => {
    setDraftSelectMode(false);
    setSelectedDraftIds(new Set());
  };

  // Transform LibraryPrompt to PromptDetailModal data format
  const getModalData = (prompt: LibraryPrompt) => ({
    score_id: prompt.score_id,
    overall_score: prompt.overall_score,
    detected_type: prompt.detected_type,
    best_prompt_extracted: prompt.best_prompt,
    dimension_scores_json: prompt.dimension_scores_json,
    evaluation_markdown: prompt.evaluation_markdown,
    chatcraft_subscores_json: prompt.chatcraft_subscores_json,
    original_content: prompt.original_content,
    submission_type: prompt.submission_type,
    points_total: prompt.points_total,
    points_base: prompt.points_base,
    points_rubric: prompt.points_rubric,
    points_bonus: prompt.points_bonus,
    user_name: null,
    created_at: prompt.created_at,
    submission_id: prompt.submission_id,
    is_public: prompt.is_public ?? false,
    user_id: user?.id,
  });

  // Transform favorite to LibraryPrompt format
  const transformFavorite = (fav: FavoritePrompt): LibraryPrompt => ({
    score_id: fav.score_id,
    overall_score: fav.overall_score,
    original_score: null,
    detected_type: fav.detected_type,
    best_prompt: fav.best_prompt,
    original_content: fav.original_content,
    submission_type: fav.submission_type,
    dimension_scores_json: fav.dimension_scores_json,
    evaluation_markdown: fav.evaluation_markdown,
    chatcraft_subscores_json: fav.chatcraft_subscores_json,
    points_total: fav.points_total,
    points_base: fav.points_base,
    points_rubric: fav.points_rubric,
    points_bonus: fav.points_bonus,
    created_at: fav.created_at,
    tags: fav.tags || [],
    favorite_count: fav.favorite_count || 0,
    remix_count: fav.remix_count || 0,
    recent_remixers: fav.recent_remixers || [],
    is_public: false, // Favorites don't need publish status
  });

  const getCategoryCounts = (prompts: Array<{ detected_type: string | null }>) => {
    const counts: Record<string, number> = {};
    for (const prompt of prompts) {
      const cat = prompt.detected_type || 'Unknown';
      counts[cat] = (counts[cat] || 0) + 1;
    }
    return counts;
  };

  const sortPrompts = <T extends { points_total: number | null; created_at: string }>(prompts: T[]) => {
    return [...prompts].sort((a, b) => {
      if (sortOption === 'points-desc' || sortOption === 'points-asc') {
        const aPoints = a.points_total || 0;
        const bPoints = b.points_total || 0;
        return sortOption === 'points-desc' ? bPoints - aPoints : aPoints - bPoints;
      }

      const aTime = new Date(a.created_at).getTime();
      const bTime = new Date(b.created_at).getTime();
      return sortOption === 'date-desc' ? bTime - aTime : aTime - bTime;
    });
  };

  const favoritePrompts = useMemo(() => favorites.map(transformFavorite), [favorites]);

  const recentCategoryCounts = useMemo(() => getCategoryCounts(recent), [recent]);
  const favoriteCategoryCounts = useMemo(() => getCategoryCounts(favoritePrompts), [favoritePrompts]);
  const activeCategoryCounts = activeTab === 'favorites' ? favoriteCategoryCounts : recentCategoryCounts;
  const activeCategoryNames = useMemo(() => Object.keys(activeCategoryCounts).sort(), [activeCategoryCounts]);
  const activeFilterTotal = activeTab === 'favorites' ? favoritePrompts.length : recent.length;
  const sortLabel = {
    'date-desc': 'Newest first',
    'date-asc': 'Oldest first',
    'points-desc': 'Points high to low',
    'points-asc': 'Points low to high',
  }[sortOption];

  const filterPromptsByCategory = <T extends { detected_type: string | null }>(prompts: T[]) => {
    if (categoryFilter === 'All') return prompts;
    return prompts.filter((prompt) => (prompt.detected_type || 'Unknown') === categoryFilter);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-40 w-full" />
          ))}
        </div>
      );
    }

    switch (activeTab) {
      case 'drafts':
        if (drafts.length === 0) {
          return <LibraryEmptyState tab="drafts" />;
        }
        return (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                {draftSelectMode ? `${selectedDraftIds.size} selected` : `${drafts.length} draft${drafts.length === 1 ? '' : 's'}`}
              </p>
              {draftSelectMode ? (
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={handleCancelDraftSelect}>
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={selectedDraftIds.size === 0}
                    onClick={() => setDraftDeleteIds(Array.from(selectedDraftIds))}
                  >
                    Delete selected
                  </Button>
                </div>
              ) : (
                <Button variant="outline" size="sm" onClick={() => setDraftSelectMode(true)}>
                  Select
                </Button>
              )}
            </div>
            {drafts.map((draft) => (
              <DraftCard
                key={draft.conversation_id}
                draft={draft}
                onContinue={handleContinueDraft}
                onDelete={handleDeleteDraft}
                onScore={handleScoreOrphan}
                isScoring={scoringDraftId === draft.conversation_id}
                selectMode={draftSelectMode}
                selected={selectedDraftIds.has(draft.conversation_id)}
                onSelectToggle={handleToggleDraftSelected}
              />
            ))}
          </div>
        );

      case 'recent': {
        const filtered = sortPrompts(filterPromptsByCategory(recent));
        if (filtered.length === 0 && recent.length === 0) {
          return <LibraryEmptyState tab="recent" onBuildPrompt={handleBuildPrompt} />;
        }
        if (filtered.length === 0) {
          return <p className="text-muted-foreground text-sm py-8 text-center">No prompts in this category.</p>;
        }
        return (
          <div className="divide-y divide-border/30">
            {filtered.map((prompt) => (
              <LibraryCard
                key={prompt.score_id}
                prompt={prompt}
                onViewDetail={setSelectedPrompt}
                onRemix={handleRemix}
                onViewRemixers={handleViewRemixers}
                onHidePrompt={handleHideOrDeletePrompt}
                onDeletePrompt={handleHideOrDeletePrompt}
                showPublishStatus={true}
                submissionId={prompt.submission_id}
              />
            ))}
          </div>
        );
      }

      case 'favorites': {
        if (favorites.length === 0) {
          return <LibraryEmptyState tab="favorites" />;
        }
        const filtered = sortPrompts(filterPromptsByCategory(favoritePrompts));
        if (filtered.length === 0) {
          return <p className="text-muted-foreground text-sm py-8 text-center">No favorites in this category.</p>;
        }
        return (
          <div className="divide-y divide-border/30">
            {filtered.map((prompt) => {
              const fav = favorites.find((item) => item.score_id === prompt.score_id);
              if (!fav) return null;
              return (
                <LibraryCard
                  key={fav.favorite_id}
                  prompt={prompt}
                  onViewDetail={(p) => setSelectedPrompt(p)}
                  onRemix={(p) => handleRemix(p)}
                  onViewRemixers={() => handleFavoriteViewRemixers(fav)}
                  ownerName={!fav.is_own_prompt ? fav.owner_name : undefined}
                  ownerInitials={!fav.is_own_prompt ? fav.owner_initials : undefined}
                />
              );
            })}
          </div>
        );
      }
    }
  };

  // Get title based on active tab
  const getTabTitle = () => {
    switch (activeTab) {
      case 'drafts':
        return 'Your Drafts';
      case 'favorites':
        return 'Your Favorites';
      default:
        return null; // handled by category tabs
    }
  };

  return (
    <MainLayout>
      <div className="p-4 md:p-6 space-y-6 max-w-4xl mx-auto">
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-bold">
            Vault
          </h1>
        </div>

        {/* Tab Title or Category Filter Tabs */}
        <div>
          {activeTab === 'recent' || activeTab === 'favorites' ? (
            <div className="flex flex-col gap-3">
              <div className="flex justify-end">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2">
                      <SlidersHorizontal className="h-4 w-4" />
                      Filter
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-60">
                    <DropdownMenuLabel>Sort by</DropdownMenuLabel>
                    <DropdownMenuRadioGroup
                      value={sortOption}
                      onValueChange={(value) => setSortOption(value as SortOption)}
                    >
                      <DropdownMenuRadioItem value="date-desc">Date: newest first</DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="date-asc">Date: oldest first</DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="points-desc">Points: high to low</DropdownMenuRadioItem>
                      <DropdownMenuRadioItem value="points-asc">Points: low to high</DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                    <DropdownMenuSeparator />
                    <div className="px-2 py-1.5 text-xs text-muted-foreground">
                      Current: {sortLabel}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-1">
                <button
                  onClick={() => setCategoryFilter('All')}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium border whitespace-nowrap transition-colors ${
                    categoryFilter === 'All'
                      ? 'bg-amber-500/20 text-amber-400 border-amber-500/50'
                      : 'bg-muted/30 text-muted-foreground border-border/50 hover:bg-muted/50'
                  }`}
                >
                  All ({activeFilterTotal})
                </button>
                {activeCategoryNames.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setCategoryFilter(cat)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border whitespace-nowrap transition-colors ${
                      categoryFilter === cat
                        ? 'bg-amber-500/20 text-amber-400 border-amber-500/50'
                        : 'bg-muted/30 text-muted-foreground border-border/50 hover:bg-muted/50'
                    }`}
                  >
                    {cat} ({activeCategoryCounts[cat]})
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <h2 className="text-lg font-semibold text-foreground">{getTabTitle()}</h2>
          )}
        </div>

        {/* Content */}
        <div className="min-h-[400px]">
          {renderContent()}
        </div>
      </div>

      {/* Detail Modal */}
      <PromptDetailModal
        isOpen={!!selectedPrompt}
        onClose={handleCloseModal}
        data={selectedPrompt ? getModalData(selectedPrompt) : null}
        loading={false}
        showUserName={false}
      />

      {/* Remixers Modal */}
      {remixersPromptId && membership?.org_id && (
        <RemixersModal
          isOpen={showRemixersModal}
          onClose={() => {
            setShowRemixersModal(false);
            setRemixersPromptId(null);
          }}
          scoreId={remixersPromptId}
          totalCount={remixersCount}
        />
      )}

      <AlertDialog
        open={draftDeleteIds.length > 0}
        onOpenChange={(open) => {
          if (!open) setDraftDeleteIds([]);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {draftDeleteIds.length === 1 ? 'this draft' : `${draftDeleteIds.length} drafts`}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will remove {draftDeleteIds.length === 1 ? 'the selected draft' : 'the selected drafts'} from
              your drafts. You cannot recover {draftDeleteIds.length === 1 ? 'it' : 'them'} after deleting.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => void handleConfirmDeleteDrafts()}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </MainLayout>
  );
}
